<script lang="ts" setup>
import { computed } from '#imports'
import NameAscend from '@/components/icons/NameAscend.vue'
import NameDescend from '@/components/icons/NameDescend.vue'
import SizeAscend from '@/components/icons/SizeAscend.vue'
import SizeDescend from '@/components/icons/SizeDescend.vue'
import TimeAscend from '@/components/icons/TimeAscend.vue'
import TimeDescend from '@/components/icons/TimeDescend.vue'
import type { OrderType } from '@/store/share.script'
import { FileOrder } from '@/types/enum'
import PinFoldersTopIcon from '~/components/icons/PinFoldersTopIcon.vue'
import useDisplayMode from '~/composables/useDisplayMode'

const emits = defineEmits<{ change: [] }>()

const { size } = defineProps<{ size?: 'small' }>()

const { orderType, pinFoldersTop } = useDisplayMode()

const orderType2Icon: { [key in OrderType]: IconComponent } = {
  [FileOrder.NAME_ASC]: NameAscend,
  [FileOrder.NAME_DESC]: NameDescend,
  [FileOrder.MODIFY_TIME_DESC]: TimeDescend,
  [FileOrder.MODIFY_TIME_ASC]: TimeAscend,
  [FileOrder.SIZE_DESC]: SizeDescend,
  [FileOrder.SIZE_ASC]: SizeAscend,
}

const orderOptions: {
  type: OrderType
  title: string
}[] = [
  {
    type: FileOrder.NAME_ASC,
    title: 'sorter-name-ascend',
  },
  {
    type: FileOrder.NAME_DESC,
    title: 'sorter-name-descend',
  },
  {
    type: FileOrder.MODIFY_TIME_DESC,
    title: 'sorter-time-descend',
  },
  {
    type: FileOrder.MODIFY_TIME_ASC,
    title: 'sorter-time-ascend',
  },
  {
    type: FileOrder.SIZE_DESC,
    title: 'sorter-size-descend',
  },
  {
    type: FileOrder.SIZE_ASC,
    title: 'sorter-size-ascend',
  },
]

const changeSortType = (val: OrderType) => {
  emits('change')
  orderType.value = val
}

const iconSize = computed(() => (size === 'small' ? 20 : 24))
</script>

<template>
  <ul
    :class="size || ''"
    class="base-sorter-shim"
  >
    <li
      v-for="item in orderOptions"
      :key="item.title"
      :class="item.type === orderType && 'active'"
      @click="() => changeSortType(item.type)"
    >
      <component :is="orderType2Icon[item.type]" />
      <span style="margin-left: 13px">
        {{ $t(item.title) }}
      </span>
    </li>
    <li class="pin-folders-top">
      <div class="label">
        <PinFoldersTopIcon :size="iconSize" />
        <span> {{ $t('pin-folders-top') }} </span>
      </div>
      <ElSwitch v-model="pinFoldersTop" />
    </li>
  </ul>
</template>

<style lang="less" scoped>
.base-sorter-shim {
  &.small {
    li {
      height: 40px;
      border-radius: 2px;
      font-size: 13px;
    }
  }

  li {
    display: flex;
    align-items: center;
    height: 48px;
    line-height: 1.5;
    padding: 0 10px;
    cursor: pointer;
    border-radius: 4px;

    &:hover,
    &.active {
      background: var(--color-hover);
    }
  }

  .pin-folders-top {
    border-top: 0.5px solid #e3e7eb;
    justify-content: space-between;
    cursor: default;

    &:hover,
    &.active {
      background: unset;
    }

    .label {
      display: flex;
      align-items: center;
      gap: 12px;

      span {
        max-width: 104px;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .base-sorter-shim {
    li {
      height: 40px;
      line-height: 40px;
      padding: 0 16px;

      &:hover {
        background: unset;
      }

      &.active {
        background: url(@/assets/images/share/selected.svg) no-repeat 172px center / 20px;
      }
    }

    .pin-folders-top {
      position: relative;
      border: 0;

      &::before {
        position: absolute;
        display: block;
        content: '';
        width: -webkit-fill-available;
        height: 0.5px;
        background-color: #e3e7eb;
        left: 0;
        top: 0;
        margin: 0 8px;
      }
    }
  }
}
</style>
